import type { Ref } from 'vue';

export interface SortByItem {
    key: string;
    order: 'asc' | 'desc';
}

const directionMultiplier = (key: string, sortBy: Ref<SortByItem[]>): number =>
    sortBy.value.find((item) => item.key === key)?.order === 'desc' ? -1 : 1;

/**
 * Build a Vuetify data-table column comparator that orders values numerically and
 * keeps non-numeric placeholders (e.g. the `--` missing indicator) last in BOTH
 * sort directions.
 *
 * Vuetify swaps the comparator's arguments for descending order, which would
 * otherwise float the placeholders to the top; multiplying the missing-value
 * branch by the active direction cancels that out. The column's reactive
 * `sort-by` model is passed in so the comparator can read that direction.
 */
export function numericColumnSort(key: string, sortBy: Ref<SortByItem[]>) {
    return (a: string | number, b: string | number): number => {
        const na = Number(a);
        const nb = Number(b);
        const aMissing = Number.isNaN(na);
        const bMissing = Number.isNaN(nb);
        if (aMissing || bMissing) return (Number(aMissing) - Number(bMissing)) * directionMultiplier(key, sortBy);
        return na - nb;
    };
}